import type { ReactNode } from 'react'
import { useTranslation } from 'next-i18next'
import { Tooltip } from 'shared/components/tooltip'
import useUser, { isAuthorizedUser } from 'shared/hooks/use-user'
import CheckCircleIcon from 'shared/icons/check-circle-icon'
import CloseCircleIcon from 'shared/icons/close-circle-icon'
import TrashIcon from 'shared/icons/trash-icon'
import { usePagination } from 'shared/route-query/hooks/use-pagination'
import type { DomainAssetsInterface } from 'shared/types/domain'
import { baseServerSideProps } from 'shared/utils/base-server-side-props'
import { formatDateTimeUtil } from 'shared/utils/date-time/format-date-time'
import { withDomainAssets } from 'shared/utils/with-domain-assets'
import { ProfileLayout } from 'modules/profile/components/profile-layout'
import { getProfilePageLayout } from 'modules/profile/layouts/get-profile-page-layout'
import CancelSubscriptionModal from 'modules/profile/manage-subscriptions/components/cancel-subscription-modal'
import type { ColumnDefinitionInterface } from 'modules/profile/manage-subscriptions/components/table/table'
import { TableActions } from 'modules/profile/manage-subscriptions/components/table/table-actions'
import { useManageSubscriptions } from 'modules/profile/manage-subscriptions/hooks/use-manage-subscriptions'
import type { ManageSubscriptionsInterface } from 'modules/profile/manage-subscriptions/types/manage-subscriptions-interface'

function ManageSubscriptions(): ReactNode {
  const { t } = useTranslation()
  const { user } = useUser()
  const authorizedUser = isAuthorizedUser(user) ? user : undefined
  const { limit, setLimit, pagination, setPagination } = usePagination({ isStaticUrl: true })
  const formatSubscriptionDate = (date: string): string | undefined =>
    formatDateTimeUtil(date, {
      dateFormat: authorizedUser?.dateFormat,
      timeFormat: authorizedUser?.timeFormat,
      timeZone: authorizedUser?.timezone,
    }).formatted

  const manageSubscriptionsColumns: ColumnDefinitionInterface<ManageSubscriptionsInterface>[] = [
    {
      key: 'subscriptionItems',
      header: t('profile.subscriptions.title_plural'),
      widthPercent: 30,
      onRender: subscription => (
        <span>{subscription.subscriptionItems[0].orderItem.pricePlan.innerName}</span>
      ),
    },
    {
      key: 'status',
      header: t('profile.subscriptions.status.title'),
      widthPercent: 40,
      onRender: subscription => {
        if (user && !isAuthorizedUser(user)) {
          return <div />
        }
        return (
          <div className="flex items-center lg:ml-4">
            <Tooltip
              label={
                subscription.status === 'active' ? (
                  t('profile.subscriptions.status.active')
                ) : subscription.subscriptionItems[0].deactivatedAt ? (
                  <>
                    {t('profile.subscriptions.status.cancelled')} -{' '}
                    {formatSubscriptionDate(subscription.subscriptionItems[0].deactivatedAt)}
                  </>
                ) : (
                  t('profile.subscriptions.status.cancelled')
                )
              }
              mode="hover"
              key="remove-tooltip"
            >
              {subscription.status === 'active' ? (
                <CheckCircleIcon className={`size-[16px] fill-green sm:size-[20px]`} />
              ) : (
                <CloseCircleIcon className={`size-[16px] fill-red sm:size-[20px]`} />
              )}
            </Tooltip>
          </div>
        )
      },
    },
    {
      key: 'createdAt',
      header: t('profile.subscriptions.date'),
      widthPercent: 30,
      onRender: subscription => {
        if (user && !isAuthorizedUser(user)) {
          return <div />
        }
        return <span>{formatSubscriptionDate(subscription.createdAt)}</span>
      },
    },
  ]
  const { data: subscriptions, mutate } = useManageSubscriptions({
    limit,
    pagination,
  })

  return (
    <ProfileLayout title={t('profile.user_settings.manage_subscriptions.title')}>
      <div className="flex h-fit w-full flex-col gap-2 overflow-x-hidden rounded-md bg-background-secondary md:gap-5">
        <TableActions
          actions={{
            custom: [
              {
                name: 'cancel-subscription',
                shouldRender: subscription => subscription.status === 'active',
                onIconRender: () => (
                  <TrashIcon className="size-[16px] fill-text-secondary hover:fill-red sm:size-[20px]" />
                ),
                tooltipCaption: t('profile.subscriptions.cancel_subscription'),
                onActionRender: (opened, onClose, subscription) => (
                  <CancelSubscriptionModal
                    mutate={mutate}
                    subscription={subscription}
                    opened={opened}
                    onClose={onClose}
                  />
                ),
              },
            ],
          }}
          data={subscriptions}
          columns={manageSubscriptionsColumns}
          limiter={{ limit, setLimit }}
          paginator={{
            pagination,
            setPagination,
            hasMore: subscriptions?.hasMore,
          }}
        />
      </div>
    </ProfileLayout>
  )
}

ManageSubscriptions.getLayout = (
  page: ReactNode,
  props: { domainAssets?: DomainAssetsInterface },
) =>
  getProfilePageLayout(page, {
    titleKey: 'profile.user_settings.manage_subscriptions.title',
    domainAssets: props.domainAssets,
  })

export const getServerSideProps = withDomainAssets(baseServerSideProps)

export default ManageSubscriptions
